import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { SelectedBrandModelService } from './selected-brand-model.service';

/**
 * Shopping routes require a phone brand + model. Anything that lands a visitor on the
 * catalog or a product before they have picked one is bounced to /selection first, with
 * the original destination carried along so they resume there after choosing.
 *
 * Gating here rather than in each caller means every entry point is covered — home tiles,
 * header/footer links, search results and external deep links alike.
 *
 * skipModelSelection=1 is a deliberate, opt-in escape hatch for links that want to show a
 * curated category browsed across all models (e.g. "Shop City Cases") before asking the
 * shopper to narrow down to their phone; only callers that set it are affected.
 */
export const modelSelectionGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree => {
    if (route.queryParamMap.get('skipModelSelection') === '1') return true;

    // the stored selection lives in localStorage; on the server there is nothing to read,
    // so let the route through and let the browser re-run the check after bootstrap.
    if (!isPlatformBrowser(inject(PLATFORM_ID))) return true;

    const selectedBrandModelService = inject(SelectedBrandModelService);
    const stored = selectedBrandModelService.get();
    if (stored?.brandId && stored?.modelId) return true;

    const router = inject(Router);
    return router.createUrlTree([ '/selection' ], {
        queryParams: {
            // keep the category context so /selection opens on the right catalog slice
            mainCategoryId: route.queryParamMap.get('mainCategoryId') || undefined,
            subCategoryId: route.queryParamMap.get('subCategoryId') || undefined,
            categoryName: route.queryParamMap.get('categoryName') || undefined,
            returnUrl: state.url
        }
    });
};
